import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, canReview, useAuth } from './lib/auth-context';
import { Spinner } from './components/ui';
import { LoginPage } from './pages/Login';
import { SetPasswordPage } from './pages/SetPassword';
import { MyReportsPage } from './pages/MyReports';
import { NewReportPage } from './pages/NewReport';
import { ReportDetailPage } from './pages/ReportDetail';
import { ReviewQueuePage } from './pages/ReviewQueue';
import { AccountingExportPage } from './pages/AccountingExport';
import { AdminSettingsPage, AdminUsersPage } from './pages/Admin';

function Shell({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const reviewer = canReview(user?.role);

  const navItems = [
    { to: '/', label: 'My reports' },
    ...(reviewer ? [{ to: '/review', label: 'Review' }, { to: '/accounting', label: 'Accounting' }] : []),
    ...(user?.role === 'admin'
      ? [{ to: '/admin/users', label: 'Staff' }, { to: '/admin/settings', label: 'Settings' }]
      : []),
  ];

  return (
    <div className="min-h-screen">
      <nav className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-4 px-4 py-3">
          <Link to="/" className="font-semibold text-brand">D2P Expenses</Link>
          <div className="flex flex-1 gap-1 overflow-x-auto">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm ${
                  pathname === item.to ? 'bg-brand-light font-medium text-brand' : 'text-slate-600'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <button type="button" onClick={() => void signOut()} className="text-sm text-slate-500 underline">
            Sign out
          </button>
        </div>
      </nav>
      <main className="mx-auto max-w-5xl px-4 py-5">{children}</main>
    </div>
  );
}

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner label="Signing you in" />;
  if (!user) return <Navigate to="/login" replace />;
  return <Shell>{children}</Shell>;
}

function RequireRole({ role, children }: { role: 'finance' | 'admin'; children: React.ReactNode }) {
  const { user } = useAuth();
  const allowed = role === 'admin' ? user?.role === 'admin' : canReview(user?.role);
  if (!allowed) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/" element={<Protected><MyReportsPage /></Protected>} />
        <Route path="/reports/new" element={<Protected><NewReportPage /></Protected>} />
        <Route path="/reports/:id" element={<Protected><ReportDetailPage /></Protected>} />
        <Route path="/review" element={<Protected><RequireRole role="finance"><ReviewQueuePage /></RequireRole></Protected>} />
        <Route path="/accounting" element={<Protected><RequireRole role="finance"><AccountingExportPage /></RequireRole></Protected>} />
        <Route path="/admin/users" element={<Protected><RequireRole role="admin"><AdminUsersPage /></RequireRole></Protected>} />
        <Route path="/admin/settings" element={<Protected><RequireRole role="admin"><AdminSettingsPage /></RequireRole></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
